import { Component, OnInit } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Rate } from '../../../core/models/entities/rate';
import { Store } from '@ngrx/store';
import { fromStore } from 'angular/app/core/store';
import { fadeAnimation } from '../../../core/animations/fade.animation';
import { RatesState } from '../../../core/store/rates/rates.state';
import {
  ModalInstance,
  ModalService,
} from '../../../core/services/modal.service';
import { CreateRateModalComponent } from '../../components/create-rate-modal/create-rate-modal.component';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RatesService } from '../../../core/services/rates.service';
import { ApiError } from '../../../core/models/common/api-error';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ratesActions } from 'angular/app/core/store/rates';
import { ToastService } from '../../../core/services/toast.service';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';

@Component({
  selector: 'app-rates-page',
  animations: [fadeAnimation],
  template: `
    <app-page-header title="Groupes de tarifs">
      <ng-template #actions>
        <app-button
          variant="primary"
          [attr.data-cy]="'add-rate'"
          (click)="createRate()"
          >
          Ajouter
        </app-button>
      </ng-template>
    </app-page-header>
    
    <app-spacer size="6"></app-spacer>
    
    @if (loading$ | async) {
      <div
        class="mt-40 flex justify-center"
        >
        <app-loader></app-loader>
      </div>
    } @else {
      <div [@fadeAnimation]="'in'">
        <app-fieldset>
          <div title>Groupes de tarifs</div>
          <div description
            >Les groupes de tarifs servent à gérer la possibilité de
            tarification différente pour certains groupes de personnes (ex :
            résidents, non-résidents).<br /><br />Lorsqu’un groupe de tarif est
            ajouté, un champ se crée dans la gestion de chaque salle, où le prix
            devra être manuellement défini par groupe tarifaire.</div
            >
            <div class="space-y-6" data-cy="rates-list">
              @for (rate of rates$ | async; track rate) {
                <div>
                  <app-list-item
                    [id]="rate.id"
                    [clickableTitle]="true"
                    (titleClicked)="editRate(rate)"
                    >
                    <app-toggle
                      [defaultValue]="rate.active"
                      (isEnabled)="toggleActive(rate, $event)"
                      toggle
                    ></app-toggle>
                    <div title class="font-semibold">
                      {{ rate.name }}
                    </div>
                    @if (editingRate?.id === rate.id) {
                      <div class="mt-2" edit>
                        <form novalidate (ngSubmit)="saveEdit()">
                          <app-text-input
                            label="Nom du groupe de tarif"
                            name="name"
                            [control]="form.get('name')"
                          ></app-text-input>
                          <div
                            class="flex items-center justify-end space-x-6 pt-3 pb-6"
                            >
                            <app-button
                              variant="link"
                              size="sm"
                              (click)="editingRate = null"
                              >Annuler</app-button
                              >
                              <app-button
                                [loading]="sendingData"
                                [attr.data-cy]="'update'"
                                type="submit"
                                variant="secondary"
                                size="sm"
                                >Valider
                              </app-button>
                            </div>
                          </form>
                        </div>
                      }
                      <div class="space-x-4" actions>
                        <app-icon-action
                          icon="pen"
                          [class.invisible]="editingRate?.id === rate.id"
                          [attr.data-cy]="'edit'"
                          (click)="editRate(rate)"
                        ></app-icon-action>
                        <app-icon-action
                          icon="trash"
                          [class.invisible]="editingRate?.id === rate.id"
                          [attr.data-cy]="'delete'"
                          (click)="deleteRate(rate)"
                        ></app-icon-action>
                      </div>
                    </app-list-item>
                  </div>
                }
              </div>
            </app-fieldset>
          </div>
        }
    
    `,
  standalone: false,
})
export class RatesPageComponent implements OnInit {
  rates$: Observable<Rate[]> = this.store.select(fromStore.selectAllRates);
  loading$: Observable<boolean> = this.store
    .select(fromStore.selectRatesState)
    .pipe(
      map((ratesState: RatesState) => {
        return ratesState.loading;
      }),
    );
  editingRate: Rate;
  form: UntypedFormGroup;
  sendingData: boolean;
  constructor(
    private store: Store,
    private modalService: ModalService,
    private ratesService: RatesService,
    private formValidationService: FormValidationService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {}

  createRate() {
    this.modalService.open(CreateRateModalComponent, {
      title: 'Ajouter un groupe de tarif',
      okAction: {
        title: 'Enregistrer',
      },
    });
  }

  editRate(rate: Rate) {
    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(rate.name, [Validators.required]),
    });
    this.editingRate = rate;
  }

  async saveEdit() {
    try {
      this.sendingData = true;
      const rate: Rate = await this.ratesService.update(
        this.editingRate,
        this.form.value,
      );
      this.store.dispatch(ratesActions.edit({ rate }));
      this.editingRate = null;
    } catch (exception) {
      this.formValidationService.displayApiValidationErrors(
        this.form,
        exception as ApiError,
      );
    } finally {
      this.sendingData = false;
    }
  }

  async toggleActive(rate: Rate, active: boolean) {
    try {
      const updatedRate: Rate = await this.ratesService.toggleActive(
        rate.id,
        active,
      );
      this.store.dispatch(ratesActions.edit({ rate: updatedRate }));
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      this.store.dispatch(
        ratesActions.edit({ rate: { ...rate, active: !active } }),
      );
    }
  }

  deleteRate(rate: Rate) {
    const modalInstance: ModalInstance = this.modalService.open(
      ConfirmModalComponent,
      {
        title: 'Supprimer un groupe de tarif',
        message: `Êtes-vous sûr·e de vouloir supprimer le groupe de tarif ${rate.name} ?`,
        okAction: {
          title: 'Supprimer',
          variant: 'danger',
        },
        cancelAction: {
          title: 'Non',
        },
      },
    );
    modalInstance.okClicked$.subscribe(async () => {
      modalInstance.saving = true;
      try {
        await this.ratesService.delete(rate.id);
        this.store.dispatch(ratesActions.deleteOne({ rate }));
        this.toastService.success(`Groupe de tarif ${rate.name} supprimé.`);
        this.modalService.close();
        modalInstance.saving = false;
      } catch (exception: any) {
        this.toastService.error(exception.message);
        modalInstance.saving = false;
      }
    });
  }
}
